import React, { useEffect, useState, useRef } from 'react';

import * as getStatic from '@backpackjs/core-nextjs/get-static';
import { store } from '@backpackjs/storefront';

// eslint-disable-next-line import/no-unresolved
import CollectionTemplate from '../../templates/collection';
import { PageErrorBoundary, PageHead } from '../../_components';

export const getStaticPaths = getStatic.paths.collection;
export const getStaticProps = getStatic.props.collection;

const filterOutDraftProducts = (product) => product.status === 'ACTIVE';

export default function Collection({ isPreview, renderSections, ...props }) {
  const [collection, setCollection] = useState(props.collection);
  const [productsReady, setProductsReady] = useState(false);
  const _setCollection = store.recoil.useSetRecoilState(store.state.collection);
  const collectionRef = useRef();
  collectionRef.current = collection;

  const fetchCache = useRef({});

  const fetchProduct = async (handle) => {
    if (!fetchCache.current[handle]) {
      const isDev = process.env.NODE_ENV === 'development';
      const siteUrl = process.env.HOST_SITE_URL || process.env.SITE_URL;
      const host = isDev ? `http://localhost:8080` : siteUrl;
      const response = await fetch(`${host}/json/products/${handle}.json`);
      fetchCache.current[handle] = await response.json();
    }

    return fetchCache.current[handle];
  };

  const loadMore = async (n = process.env.COLLECTIONS_RESOLVE_FIRST) => {
    let i = 0;

    const promises = collectionRef.current.products.map((product) => {
      if (!product.variants && i < n) {
        i += 1;
        return fetchProduct(product.handle);
      }
      return Promise.resolve(product);
    });

    const products = await Promise.all(promises);
    const updatedCollection = { ...collectionRef.current, products };

    setCollection(updatedCollection);

    return updatedCollection;
  };

  const hasAllProductsOnLoad =
    collection.products.length <
    parseInt(process.env.COLLECTIONS_RESOLVE_FIRST, 10);

  useEffect(() => {
    fetch(`/json/collections/products/${collection.handle}.json`)
      .then((response) => response.json())
      .then((result) => {
        const remainingProducts = isPreview
          ? result
          : result.filter(filterOutDraftProducts);
        setCollection({
          ...collection,
          products: [...collection.products, ...remainingProducts],
        });
        setProductsReady(true);
      });

    return () => {
      setCollection(null);
      _setCollection(null);
      setProductsReady(false);
    };
  }, []);

  useEffect(() => {
    _setCollection(collection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection.handle]);

  return (
    <PageErrorBoundary>
      <PageHead page={props.page}>
        <CollectionTemplate
          {...props}
          collection={collection}
          loadMore={loadMore}
          productsReady={hasAllProductsOnLoad || productsReady}
          renderSections={renderSections}
        />
      </PageHead>
    </PageErrorBoundary>
  );
}
